import { SessionInputs, WaveInputs, SkillStates, LocationEnum, Location, WaveTime } from '../types'
import LOCATIONS from '../constants/locations'
import { getAdjustedSkillLevel, getRandomInRange, getRandomWeightedValue } from '@/utils'

export const waveTimeRanges: { [key in WaveTime]: [number, number] } = {
  less_than_1: [0, 1],
  one_to_two: [1, 2],
  three_to_five: [3, 5],
  six_to_ten: [6, 10],
  eleven_to_twenty: [11, 20],
  twenty_one_to_forty: [21, 40],
  more_than_forty: [41, 60]
}

export function getCatchProbability(
  skills: SkillStates,
  sessionInputs: SessionInputs,
  waveSpeed: string,
  isWhiteWater: boolean
): number {
  const crowdImpact = sessionInputs.crowd === 'packed' ? 0.5 : sessionInputs.crowd === 'big_crowd' ? 0.7 : 1.0
  const skillImpact =
    sessionInputs.crowdSkill === 'pros'
      ? 0.2
      : sessionInputs.crowdSkill === 'advanced'
      ? 0.4
      : sessionInputs.crowdSkill === 'intermediates'
      ? 0.7
      : 1.0
  const waveSpeedImpact = waveSpeed === 'fast' ? 0.8 : 1.0
  const waveTypeImpact =
    sessionInputs.waveType === 'slab'
      ? 0.3
      : sessionInputs.waveType === 'hollow'
      ? 0.4
      : sessionInputs.waveType === 'punchy'
      ? 0.7
      : sessionInputs.waveType === 'easy'
      ? 0.9
      : 1.0

  const waveHeightImpactMap = {
    knee_high: 1,
    waist_high: 0.95,
    shoulder_high: 0.8,
    head_high: 0.7,
    overhead: 0.55,
    one_and_half_overhead: 0.4,
    two_overhead: 0.3,
    three_overhead: 0.1,
    big_wave: 0.05
  } as const

  type WaveHeight = keyof typeof waveHeightImpactMap
  const waveHeightImpact = waveHeightImpactMap[sessionInputs.waveHeight as WaveHeight]

  const popupSkillImpact = 0.8 + getAdjustedSkillLevel(skills.popup.level)
  const fitnessImpact = 0.8 + getAdjustedSkillLevel(skills.fitness.level) * waveHeightImpact
  const paddlingImpact = 0.8 + getAdjustedSkillLevel(skills.paddling.level) * crowdImpact
  const waveReadingImpact = 0.8 + getAdjustedSkillLevel(skills.wave_reading.level) * skillImpact

  const whiteWaterCatchProbabilityBoost = isWhiteWater ? 0.2 : 0
  const baseCatchProbability =
    waveTypeImpact * waveSpeedImpact * popupSkillImpact * fitnessImpact * paddlingImpact * waveReadingImpact

  let probability = Math.min(
    0.97,
    Math.max(
      0,
      getRandomInRange(
        baseCatchProbability - baseCatchProbability / 10,
        baseCatchProbability + baseCatchProbability / 10
      ) + whiteWaterCatchProbabilityBoost
    )
  )
  return probability
}

export function generateWaveInputs(
  location: string,
  sessionInputs: SessionInputs,
  skills: SkillStates,
  isWhiteWater: boolean
): WaveInputs {
  const surfBreak: Location = LOCATIONS[location as LocationEnum]
  const waveSpeed = getRandomWeightedValue(surfBreak.waveSpeed)
  const waveTimeRange = getRandomWeightedValue(surfBreak.waveTime)

  const catchProbability = getCatchProbability(skills, sessionInputs, waveSpeed, isWhiteWater)
  const waveTime = getRandomInRange(...waveTimeRanges[waveTimeRange as WaveTime])
  const wavePeriod = sessionInputs.wavePeriod
  const waitingForWaveTime = getRandomInRange(0.5 * wavePeriod, 1.5 * wavePeriod)

  return {
    waveSpeed,
    waveTime,
    catchProbability,
    waitingForWaveTime
  }
}
